// Realm-entry / realm-exit trigger discovery. Pure. Given a
// snapshot of cards in play, return the watchers that should react
// to a card entering or leaving the realm. The orchestrator
// (GameBoard) then fires each watcher via applyCardAutomation and
// uses pushSimultaneous to land the resulting events on the
// storyline in codex active-vs-non-active order.
//
// ── Codex anchors ──
//
// Storyline section: "If the resolution of one event triggers a new
//   event, the new event will be inserted into the storyline just
//   before the triggering event."
// Simultaneous Triggers: "the active player adds all of their events
//   first, in the order of their choice. Then, the non-active player
//   add their events in the order of their choice."
//
// ── Trigger types this module discovers ──
//
//   TRIGGER.ON_REALM_ENTRY. Watcher fires when ANY card enters the
//     realm. Common pattern: "Each non-Airborne minion that enters
//     here dies." Watcher's automation entries must have
//     `trigger: 'on_realm_entry'` after parser support lands.
//   TRIGGER.ON_REALM_EXIT. Watcher fires when ANY card leaves the
//     realm (cemetery, banished, hand, deck). Distinct from
//     TRIGGER.ON_DEATH which fires on the dying card itself.
//
// The triggering (entering/leaving) card is EXCLUDED from the
// watcher list. A card can't react to its own entry/exit through
// these triggers (use ON_PLAY / ON_DEATH for self-reactions).

/**
 * Find every card on the board whose automation has at least one
 * entry matching the given trigger type.
 *
 * @param {string} triggerType  e.g. 'on_realm_entry' or 'on_realm_exit'
 * @param {Array}  boardSnapshot  Array of { card, fullCard, controller }
 *                                where card is the cardInstance and
 *                                fullCard is the catalog entry with
 *                                .automation populated.
 * @param {string} [excludeCardId]  Instance id to exclude (the
 *                                  triggering card itself).
 * @returns {Array} Same shape entries that have at least one matching
 *                  trigger entry. Order: as encountered in
 *                  boardSnapshot (caller is responsible for any
 *                  desired sort).
 */
import { isCardSilenced } from '../core/keywordQuery';
import { isDisabled } from './scope';

export function findRealmTriggerWatchers(triggerType, boardSnapshot, excludeCardId) {
  if (!triggerType || !Array.isArray(boardSnapshot)) return [];
  const out = [];
  for (const entry of boardSnapshot) {
    if (!entry?.card || !entry.fullCard) continue;
    if (excludeCardId && entry.card.id === excludeCardId) continue;
    // Silenced/disabled watchers can't react. Their box text is offline.
    if (isCardSilenced(entry.card) || isDisabled(entry.card)) continue;
    const automation = entry.fullCard.automation;
    if (!Array.isArray(automation) || automation.length === 0) continue;
    if (!automation.some((a) => a.trigger === triggerType)) continue;
    out.push(entry);
  }
  return out;
}

/**
 * Partition watchers into the codex active / non-active groups.
 * The caller pushes active watchers first (deeper in the stack),
 * then non-active (top of stack → resolve first). Used together
 * with storyline.pushSimultaneous.
 */
export function partitionByPriority(watchers, activePlayer) {
  const active   = [];
  const nonActive = [];
  for (const w of watchers) {
    if (w.controller === activePlayer) active.push(w);
    else nonActive.push(w);
  }
  return { active, nonActive };
}
